/**
 * emailShell (#368) — the single email-safe HTML wrapper for ALL send paths.
 *
 * Email clients (Outlook especially) need table-based layout, a 600px content
 * column, inline styles, and a hidden preheader. Web-app HTML/Tailwind markup
 * degrades badly. Before this util the three send paths each did their own
 * thing — the primary wizard path shipped bare marked() output with no
 * wrapper at all.
 *
 * EmailService.sendNewsletter is the one integration point (all send paths
 * funnel through it). Callers may hand in a fragment OR a full document —
 * full documents are unwrapped first so the output never nests <html> tags.
 */

import { escapeHtmlText } from './escapeHtml.js';
import { htmlToPlainText } from './htmlTextBridge.js';

const SHELL_MARKER = 'data-fn-email-shell="1"';

/**
 * Pull the renderable content out of a full HTML document. Fragments pass
 * through unchanged. head/style/script wrappers are dropped.
 */
export function extractBodyContent(html) {
  if (!html || typeof html !== 'string') return '';

  if (!/<html[\s>]/i.test(html) && !/<body[\s>]/i.test(html)) {
    return html;
  }

  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    return bodyMatch[1].trim();
  }

  // Document without an explicit <body> — strip the document chrome.
  return html
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<\/?body[^>]*>/gi, '')
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .trim();
}

/**
 * Wrap newsletter content in the shared email shell.
 * Idempotent: content already carrying the shell marker is returned as-is.
 *
 * @param {Object} options
 * @param {string} options.bodyHtml  - fragment or full document
 * @param {string} [options.preheader] - hidden inbox preview text (plain text)
 * @param {string} [options.title]     - document title (plain text)
 */
export function buildEmailShell({ bodyHtml = '', preheader = '', title = '' } = {}) {
  if (typeof bodyHtml === 'string' && bodyHtml.includes(SHELL_MARKER)) {
    return bodyHtml;
  }

  const content = extractBodyContent(bodyHtml || '');
  const safePreheader = escapeHtmlText(preheader).trim();
  const safeTitle = escapeHtmlText(title);

  // The &nbsp;&zwnj; run pads the preheader so clients don't pull body text
  // into the inbox preview line.
  const preheaderBlock = safePreheader
    ? `<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;">${safePreheader}${'&nbsp;&zwnj;'.repeat(20)}</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<title>${safeTitle}</title>
</head>
<body style="margin:0;padding:0;background-color:#f4f4f7;-webkit-text-size-adjust:100%;">
${preheaderBlock}
<table role="presentation" ${SHELL_MARKER} width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f4f7;">
  <tr>
    <td align="center" style="padding:24px 12px;">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:100%;background-color:#ffffff;border-radius:8px;">
        <tr>
          <td style="padding:32px 40px;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.6;color:#333333;text-align:left;">
${content}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}

// Block-aware HTML → plain text for the text/plain MIME part.
// Review consolidation: this delegates to the codebase's canonical
// converter in htmlTextBridge (extended with head-strip + table/blockquote
// handling) instead of maintaining a parallel implementation here.
export { htmlToPlainText };

export default { buildEmailShell, extractBodyContent, htmlToPlainText };
